import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { FormEvent, useCallback, useState } from "react";

import { useToast } from "@/components/ui";
import { useStore } from "@/store";
import { getPostLoginRedirectPath } from "@/utils/authUtils";
import { tokenStorage } from "@/utils/tokenStorage";
import { hasAdminRole } from "@/utils/routeGuards";

export const useVerifyOTPForm = () => {
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();
  const router = useRouter();
  const navigate = useNavigate();
  const { from: returnPath } = useSearch({ from: "/auth/verify-otp" });

  const verifyTwoFactorLogin = useStore.use.verifyTwoFactorLogin();
  const invalidateCompanies = useStore.use.invalidateCompanies();

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (otp.length !== 6) {
        toast({
          variant: "destructive",
          title: "Invalid OTP",
          description: "Please enter a 6-digit code",
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const result = await verifyTwoFactorLogin(otp);
        if (!result.success) {
          toast({
            variant: "destructive",
            title: result.error ?? "Verification failed",
          });
          return;
        }

        // Invalidate companies cache
        invalidateCompanies();

        // Post-auth verification flow:
        // If user has NOT verified either email or phone, force them into the verification method selection.
        const state = useStore.getState();
        if (!state.profile) {
          await state.fetchProfile({ force: true });
        }
        const profile = useStore.getState().profile;

        if (profile && !profile.email_verified && !profile.phone_verified) {
          await router
            .invalidate()
            .finally(() => {
              void navigate({ to: "/auth/verify-method" });
            })
            .catch(console.error);
          return;
        }

        const redirectPath = getPostLoginRedirectPath({
          from: returnPath,
          isAdmin: hasAdminRole(result.roles),
        });

        await router
          .invalidate()
          .finally(() => {
            window.location.assign(redirectPath);
          })
          .catch((error: unknown) => {
            console.error("[auth-otp] Failed to invalidate router after verification", { error, redirectPath });
          });
      } catch (err) {
        console.error("[auth-otp] OTP verification failed", { error: err });
        toast({
          variant: "destructive",
          title: "Verification failed",
          description: "An error occurred while verifying your OTP. Please try again.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [otp, toast, verifyTwoFactorLogin, invalidateCompanies, navigate, router, returnPath]
  );

  const handleCancel = useCallback(() => {
    // Clear any temporary auth state
    tokenStorage.clearAuthCookies();
    void navigate({ to: "/auth/login", search: { from: undefined } });
  }, [navigate]);

  return {
    otp,
    setOtp,
    isSubmitting,
    handleSubmit,
    handleCancel,
  };
};
